<template>
  <WebLayout :title="`Estado de cuenta`">
    <Head :title="`Estado de cuenta`" />
    <div class="min-h-screen bg-gray-50 py-8 px-4">
      <div class="w-full max-w-2xl mx-auto space-y-6">
        <!-- Header Card -->
        <div class="bg-white rounded-2xl shadow-lg border border-gray-100 p-6">
          <div class="flex flex-col items-center mb-5">
            <div class="w-16 h-16 bg-teal-100 rounded-full flex items-center justify-center mb-3">
              <i class="fa fa-user-circle text-3xl text-teal-600" />
            </div>
            <h1 class="text-2xl font-bold text-teal-800 text-center">Estado de Cuenta</h1>
            <p class="text-gray-500 text-sm text-center mt-1">
              Consulta tu estado de cuenta en
              <strong>{{ client?.account?.name || 'nuestra plataforma' }}</strong>
            </p>
          </div>

          <!-- Client Info -->
          <div class="grid grid-cols-2 gap-2 text-sm text-gray-600 bg-gray-50 rounded-xl p-4">
            <div class="flex items-center gap-2">
              <i class="fa fa-user text-teal-400 w-4" />
              <span>{{ client.name }}</span>
            </div>
            <div class="flex items-center gap-2">
              <i class="fa fa-address-card text-gray-400 w-4" />
              <span>{{ client.identification }}</span>
            </div>
            <div v-if="client.email" class="flex items-center gap-2">
              <i class="fa fa-envelope text-gray-400 w-4" />
              <span>{{ client.email }}</span>
            </div>
            <div v-if="client.phone" class="flex items-center gap-2">
              <i class="fa fa-phone text-gray-400 w-4" />
              <span>{{ client.phone }}</span>
            </div>
          </div>

          <!-- Balance Summary -->
          <div class="grid grid-cols-2 gap-4 mt-4">
            <div class="bg-white border border-gray-100 rounded-xl p-4 shadow-sm">
              <div class="text-sm text-gray-500 mb-1">Saldo Actual</div>
              <div
                class="text-2xl font-bold"
                :class="client.balance > 0 ? 'text-red-600' : 'text-green-600'"
              >
                ${{ Math.abs(client.balance).toFixed(2) }}
              </div>
              <div
                class="text-xs font-medium mt-1"
                :class="client.balance > 0 ? 'text-red-400' : 'text-green-400'"
              >
                {{ client.balance > 0 ? '⚠ Monto pendiente' : '✓ Al día' }}
              </div>
            </div>
            <div class="bg-white border border-gray-100 rounded-xl p-4 shadow-sm">
              <div class="text-sm text-gray-500 mb-1">Total Comprado</div>
              <div class="text-2xl font-bold text-teal-700">${{ totalPurchases.toFixed(2) }}</div>
              <div class="text-xs text-gray-400 mt-1">{{ orders.length }} órdenes</div>
            </div>
          </div>
        </div>

        <!-- === PAYMENT SECTION === -->
        <div v-if="pendingOrders && pendingOrders.length > 0" class="bg-white rounded-2xl shadow-lg border border-teal-100 overflow-hidden">
          <div class="bg-gradient-to-r from-teal-600 to-teal-700 px-6 py-4 flex items-center gap-3">
            <i class="fa fa-credit-card text-white text-lg" />
            <h2 class="text-white font-bold text-lg">Registrar Pago</h2>
          </div>

          <div class="p-6">
            <p class="text-sm text-gray-500 mb-4">
              Tienes <strong class="text-red-600">{{ pendingOrders.length }} orden(es)</strong> con saldo pendiente.
              Selecciona una y registra tu pago. Será revisado y aprobado por nuestro equipo.
            </p>

            <!-- Success message -->
            <div
              v-if="paySuccess"
              class="mb-4 p-4 bg-green-50 border border-green-200 rounded-xl flex items-center gap-3 text-green-700 text-sm"
            >
              <i class="fa fa-check-circle text-green-500 text-lg" />
              <span>¡Tu pago fue registrado! Será revisado y confirmado pronto.</span>
            </div>

            <form class="space-y-4" @submit.prevent="submitPayment">
              <!-- Order selector -->
              <div>
                <label class="block text-sm font-semibold text-gray-700 mb-1">Orden a Pagar</label>
                <select
                  v-model="payForm.order_uuid"
                  class="w-full px-4 py-2.5 rounded-xl border border-gray-200 focus:border-teal-400 focus:ring-1 focus:ring-teal-300 text-sm bg-gray-50 outline-none transition"
                >
                  <option value="">Seleccionar orden...</option>
                  <option v-for="order in pendingOrders" :key="order.id" :value="order.uuid">
                    {{ order.number_order || 'Orden #' + order.id }} — Pendiente: ${{ Number(order.pending_balance).toFixed(2) }}
                  </option>
                </select>
                <p v-if="payErrors.order" class="text-red-500 text-xs mt-1">{{ payErrors.order }}</p>
              </div>

              <div class="grid grid-cols-2 gap-4">
                <!-- Amount -->
                <div>
                  <label class="block text-sm font-semibold text-gray-700 mb-1">Monto (USD)</label>
                  <div class="relative">
                    <span class="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">$</span>
                    <input
                      v-model="payForm.amount" type="number" step="0.01" min="0.01"
                      placeholder="0.00"
                      class="w-full pl-7 pr-4 py-2.5 rounded-xl border border-gray-200 focus:border-teal-400 focus:ring-1 focus:ring-teal-300 text-sm bg-gray-50 outline-none transition"
                    />
                  </div>
                  <p v-if="payErrors.amount" class="text-red-500 text-xs mt-1">{{ payErrors.amount }}</p>
                </div>

                <!-- Method -->
                <div>
                  <label class="block text-sm font-semibold text-gray-700 mb-1">Método</label>
                  <select
                    v-model="payForm.method"
                    class="w-full px-4 py-2.5 rounded-xl border border-gray-200 focus:border-teal-400 focus:ring-1 focus:ring-teal-300 text-sm bg-gray-50 outline-none transition"
                  >
                    <option value="">Seleccionar...</option>
                    <option value="Zelle">Zelle</option>
                    <option value="Transferencia">Transferencia</option>
                    <option value="Pago Móvil">Pago Móvil</option>
                    <option value="Efectivo">Efectivo</option>
                  </select>
                  <p v-if="payErrors.method" class="text-red-500 text-xs mt-1">{{ payErrors.method }}</p>
                </div>
              </div>

              <!-- Reference -->
              <div>
                <label class="block text-sm font-semibold text-gray-700 mb-1">Referencia / Confirmación</label>
                <input
                  v-model="payForm.reference" type="text"
                  placeholder="Número de referencia o confirmación"
                  class="w-full px-4 py-2.5 rounded-xl border border-gray-200 focus:border-teal-400 focus:ring-1 focus:ring-teal-300 text-sm bg-gray-50 outline-none transition"
                />
              </div>

              <!-- Voucher -->
              <div>
                <label class="block text-sm font-semibold text-gray-700 mb-1">
                  Comprobante <span class="font-normal text-gray-400">(opcional)</span>
                </label>
                <label class="flex items-center gap-3 px-4 py-3 border-2 border-dashed border-gray-200 rounded-xl cursor-pointer hover:border-teal-400 transition-all group">
                  <i class="fa fa-upload text-gray-300 group-hover:text-teal-500 transition-colors text-xl" />
                  <span class="text-sm text-gray-400 group-hover:text-teal-600 transition-colors">
                    {{ voucherName || 'Subir imagen o PDF del comprobante' }}
                  </span>
                  <input type="file" class="hidden" accept="image/*,.pdf" @change="handleVoucher" />
                </label>
              </div>

              <button
                type="submit" :disabled="submitting"
                class="w-full py-3 bg-teal-600 hover:bg-teal-700 text-white font-bold rounded-xl transition-all shadow-md flex items-center justify-center gap-2 disabled:opacity-50"
              >
                <i class="fa fa-paper-plane" />
                {{ submitting ? 'Enviando...' : 'Enviar Pago' }}
              </button>
            </form>
          </div>
        </div>

        <!-- No pending orders notice -->
        <div
          v-else-if="orders.length > 0"
          class="bg-green-50 border border-green-200 rounded-2xl p-6 flex items-center gap-4"
        >
          <div class="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
            <i class="fa fa-check text-green-600 text-lg" />
          </div>
          <div>
            <h3 class="font-bold text-green-800">¡Cuenta al día!</h3>
            <p class="text-green-600 text-sm">No tienes saldos pendientes. ¡Gracias por tu puntualidad!</p>
          </div>
        </div>

        <!-- Order History -->
        <div class="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
          <div class="px-6 py-4 border-b border-gray-50 flex items-center gap-2">
            <i class="fa fa-history text-teal-500" />
            <h2 class="font-semibold text-gray-800">Historial de Compras</h2>
          </div>
          <div v-if="orders.length" class="divide-y divide-gray-50">
            <div
              v-for="order in orders" :key="order.id"
              class="px-6 py-4 hover:bg-gray-50 transition-colors"
            >
              <div class="flex justify-between items-start">
                <div>
                  <div class="font-semibold text-gray-800">Orden #{{ order.id }}</div>
                  <div class="text-sm text-gray-400">{{ formatDate(order.created_at) }}</div>
                </div>
                <div class="text-right">
                  <div class="font-bold text-gray-900">${{ Number(order.total).toFixed(2) }}</div>
                  <span
                    :class="{
                      'bg-green-100 text-green-700': order.order_state === 'finish',
                      'bg-yellow-100 text-yellow-700': order.order_state === 'request',
                      'bg-red-100 text-red-700': order.order_state === 'cancel'
                    }" class="text-[10px] font-bold uppercase px-2 py-0.5 rounded-full"
                  >
                    {{ getOrderState(order.order_state) }}
                  </span>
                </div>
              </div>
              <div v-if="order.pending_balance > 0" class="mt-2 text-xs text-red-500 font-medium">
                <i class="fa fa-exclamation-circle mr-1" />
                Pendiente: ${{ Number(order.pending_balance).toFixed(2) }}
              </div>
              <a
                :href="`/public/orders/${order.uuid}`"
                class="inline-flex items-center gap-1 text-teal-600 text-xs hover:text-teal-800 mt-2 font-medium"
              >
                Ver detalles <i class="fa fa-arrow-right text-[10px]" />
              </a>
            </div>
          </div>
          <div v-else class="py-10 text-center text-gray-400 text-sm italic">
            No hay compras registradas
          </div>
        </div>

        <!-- Payment History -->
        <div class="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
          <div class="px-6 py-4 border-b border-gray-50 flex items-center gap-2">
            <i class="fa fa-credit-card text-teal-500" />
            <h2 class="font-semibold text-gray-800">Historial de Pagos</h2>
          </div>
          <div v-if="payments.length" class="divide-y divide-gray-50">
            <div v-for="payment in payments" :key="payment.id" class="px-6 py-4">
              <div class="flex justify-between items-center">
                <div>
                  <div class="font-semibold text-gray-700 text-sm">{{ payment.method }}</div>
                  <div class="text-xs text-gray-400">{{ formatDate(payment.created_at) }}</div>
                  <div v-if="payment.reference" class="text-xs text-gray-400">Ref: {{ payment.reference }}</div>
                </div>
                <div class="text-right">
                  <div class="font-bold text-green-700">${{ Number(payment.amount).toFixed(2) }}</div>
                  <span
                    :class="{
                      'bg-green-100 text-green-700': payment.status === 'approve',
                      'bg-yellow-100 text-yellow-700': payment.status === 'pending',
                      'bg-red-100 text-red-700': payment.status === 'reject'
                    }" class="text-[10px] font-bold uppercase px-2 py-0.5 rounded-full"
                  >
                    {{ getPaymentStatus(payment.status) }}
                  </span>
                </div>
              </div>
            </div>
          </div>
          <div v-else class="py-10 text-center text-gray-400 text-sm italic">
            No hay pagos registrados
          </div>
        </div>

        <div class="text-center text-gray-400 text-xs py-4">
          Esta página es para consulta y registro de pagos. Para dudas, contacta a tu asesor.
        </div>
      </div>
    </div>
  </WebLayout>
</template>

<script>
import { Head } from '@inertiajs/inertia-vue3'
import WebLayout from '@/Layouts/WebLayout.vue'
import axios from 'axios'

export default {
  components: { Head, WebLayout },

  props: {
    client: { type: Object, required: true },
    orders: { type: Array, default: () => [] },
    payments: { type: Array, default: () => [] },
    pendingOrders: { type: Array, default: () => [] },
  },

  data() {
    return {
      payForm: {
        order_uuid: '',
        amount: '',
        method: '',
        reference: '',
        voucher: null,
      },
      payErrors: {},
      submitting: false,
      paySuccess: false,
      voucherName: '',
    }
  },

  computed: {
    totalPurchases() {
      return this.orders.reduce((total, order) => total + Number(order.total), 0)
    },
  },

  methods: {
    handleVoucher(e) {
      const file = e.target.files[0]
      if (file) {
        this.payForm.voucher = file
        this.voucherName = file.name
      }
    },

    validateForm() {
      this.payErrors = {}
      if (!this.payForm.order_uuid) this.payErrors.order = 'Selecciona una orden'
      if (!this.payForm.amount || this.payForm.amount <= 0) this.payErrors.amount = 'Monto inválido'
      if (!this.payForm.method) this.payErrors.method = 'Selecciona un método'
      return Object.keys(this.payErrors).length === 0
    },

    async submitPayment() {
      if (!this.validateForm()) return
      this.submitting = true

      try {
        const formData = new FormData()
        formData.append('method', this.payForm.method)
        formData.append('amount', this.payForm.amount)
        if (this.payForm.reference) formData.append('reference', this.payForm.reference)
        if (this.payForm.voucher) formData.append('voucher', this.payForm.voucher)

        await axios.post(`/public/orders/${this.payForm.order_uuid}/pay`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        })

        this.paySuccess = true
        this.payForm = { order_uuid: '', amount: '', method: '', reference: '', voucher: null }
        this.voucherName = ''

        // Scroll to top to show success
        window.scrollTo({ top: 0, behavior: 'smooth' })
      } catch (err) {
        alert('Hubo un error al enviar el pago. Intenta nuevamente.')
      } finally {
        this.submitting = false
      }
    },

    formatDate(date) {
      if (!date) return '-'
      return new Date(date).toLocaleDateString('es-VE', {
        year: 'numeric', month: 'short', day: 'numeric',
      })
    },

    getOrderState(state) {
      const states = { finish: 'Completada', request: 'Pendiente', cancel: 'Anulada', pay: 'Pagada', delivery: 'Entregada' }
      return states[state] || state
    },

    getPaymentStatus(status) {
      const statuses = { approve: 'Aprobado', pending: 'Pendiente', reject: 'Rechazado' }
      return statuses[status] || status
    },
  },
}
</script>